import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, ShoppingBag, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { SHOPIFY } from "../mock/mock";

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, count } =
    useCart();
  const navigate = useNavigate();

  const freeShippingThreshold = 99;
  const remaining = Math.max(0, freeShippingThreshold - subtotal);
  const progress = Math.min(100, (subtotal / freeShippingThreshold) * 100);

  const goToShopifyCheckout = () => {
    // Build a real Shopify cart permalink with all items -> redirects to checkout
    const parts = items
      .filter((i) => i.variant_id)
      .map((i) => `${i.variant_id}:${i.qty}`)
      .join(",");
    if (!parts) return;
    window.location.href = `${SHOPIFY.domain}/cart/${parts}`;
  };

  return (
    <div
      className={`fixed inset-0 z-[70] transition-opacity duration-300 ${
        isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={() => setIsOpen(false)}
      />
      <aside
        className={`absolute right-0 top-0 h-full w-full max-w-md bg-[#ffffff] border-l border-[#e7e0d3] flex flex-col transition-transform duration-500 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#e7e0d3]">
          <h3 className="text-sm tracking-[0.25em] uppercase text-[#17140f] flex items-center gap-2">
            Warenkorb
            <span className="text-gold">({count})</span>
          </h3>
          <button
            onClick={() => setIsOpen(false)}
            className="text-[#4a463c] hover:text-gold transition-colors"
            aria-label="Schließen"
          >
            <X size={22} />
          </button>
        </div>

        {/* Free shipping bar */}
        {items.length > 0 && (
          <div className="px-6 py-4 border-b border-[#e7e0d3]">
            <p className="text-xs text-[#4a463c] mb-2">
              {remaining > 0 ? (
                <>
                  Noch <span className="text-gold">€{remaining.toFixed(2)}</span> bis
                  zum kostenlosen Versand
                </>
              ) : (
                <span className="text-gold">Kostenloser Versand freigeschaltet ✓</span>
              )}
            </p>
            <div className="h-[3px] bg-[#e7e0d3] rounded-full overflow-hidden">
              <div
                className="h-full bg-gold transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <ShoppingBag size={40} className="text-[#d6cebf] mb-4" />
              <p className="text-[#4a463c] mb-1">Dein Warenkorb ist leer</p>
              <p className="text-[#8d8779] text-sm mb-6">
                Entdecke unsere königlichen Düfte.
              </p>
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate("/shop");
                }}
                className="border border-gold text-gold text-[11px] tracking-[0.2em] uppercase px-8 py-3 hover:bg-gold hover:text-[#14110c] transition-colors"
              >
                Jetzt einkaufen
              </button>
            </div>
          ) : (
            <ul className="space-y-6">
              {items.map((item) => (
                <li key={item.variant_id} className="flex gap-4">
                  <div className="w-20 h-24 bg-[#ece4d6] overflow-hidden flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between">
                      <div>
                        <p className="text-[#17140f] text-sm tracking-wide">
                          {item.name}
                        </p>
                        <p className="text-[#8d8779] text-xs mt-0.5">{item.size}</p>
                      </div>
                      <button
                        onClick={() => removeItem(item.variant_id)}
                        className="text-[#8d8779] hover:text-destructive transition-colors h-fit"
                        aria-label="Entfernen"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="flex justify-between items-center mt-auto pt-3">
                      <div className="flex items-center border border-[#dcd4c5]">
                        <button
                          onClick={() => updateQty(item.variant_id, item.qty - 1)}
                          className="px-2 py-1.5 text-[#4a463c] hover:text-gold transition-colors"
                        >
                          <Minus size={13} />
                        </button>
                        <span className="px-3 text-sm text-[#17140f]">
                          {item.qty}
                        </span>
                        <button
                          onClick={() => updateQty(item.variant_id, item.qty + 1)}
                          className="px-2 py-1.5 text-[#4a463c] hover:text-gold transition-colors"
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                      <span className="text-gold text-sm">
                        €{(item.price * item.qty).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-[#e7e0d3] px-6 py-5">
            <div className="flex justify-between mb-1">
              <span className="text-[#4a463c] text-sm tracking-wide">
                Zwischensumme
              </span>
              <span className="text-[#17140f]">€{subtotal.toFixed(2)}</span>
            </div>
            <p className="text-[#8d8779] text-xs mb-4">
              Versand & Steuern werden im Shopify-Checkout berechnet.
            </p>
            <button
              onClick={goToShopifyCheckout}
              className="w-full bg-gold text-[#14110c] text-[12px] tracking-[0.25em] uppercase py-4 font-medium hover:bg-[#d9b45c] transition-colors"
            >
              Zur Kasse
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                navigate("/shop");
              }}
              className="w-full text-[#4a463c] text-[11px] tracking-[0.2em] uppercase py-3 mt-1 hover:text-gold transition-colors"
            >
              Weiter einkaufen
            </button>
          </div>
        )}
      </aside>
    </div>
  );
};

export default CartDrawer;
